import type { AnchorInfo, CodeBlockRedlineItem, DocParts, UUID } from '@aline/common';
import {
  createMarkdownLocationLink,
  extractBlockAnchorsFromAlineDoc,
  extractParagraphsFromAlineDoc,
  formatAnchorId,
} from '@aline/common';
import { hash } from 'spark-md5';

/**
 * The live document's anchors, keyed the way proposal cards reference them,
 * so stale checks compare stored fingerprints against the document as it
 * stands now.
 *
 * Only an Aline document has live Slate content. A PDF renders in the Apryse
 * viewer, and its editor never leaves the placeholder children, whose ids
 * never match the PDF's coordinate anchors. Judging a PDF run against them
 * reads every suggestion as stale, and auto-rerun then reruns every rule
 * that has one, forever. A PDF cannot be edited in place, so its map stays
 * empty and nothing on it is stale.
 */
export function getLiveAnchorInfoByKey(options: {
  docParts: DocParts;
  documentId: UUID;
  isAlineDocument: boolean;
}): Map<string, AnchorInfo> {
  const { docParts, documentId, isAlineDocument } = options;
  const map = new Map<string, AnchorInfo>();
  if (!isAlineDocument) return map;

  for (const paragraph of extractParagraphsFromAlineDoc(docParts)) {
    map.set(paragraph.locationAnchor, {
      cellRanges: paragraph.cellRanges,
      documentId,
      documentName: '',
      fullText: paragraph.segments.map(segment => segment.text).join(''),
      kind: 'paragraph',
      listLevel: paragraph.listLevel,
      listMarker: paragraph.listMarker,
      locationAnchor: paragraph.locationAnchor,
      locationLink: createMarkdownLocationLink(documentId, 'live', paragraph.locationAnchor),
      section: paragraph.section,
      segments: paragraph.segments,
      text: paragraph.segments
        .filter(segment => segment.edit !== 'remove')
        .map(segment => segment.text)
        .join(''),
      type: paragraph.type,
      versionId: 'live',
    });
  }
  for (const blockAnchor of extractBlockAnchorsFromAlineDoc(docParts)) {
    map.set(formatAnchorId(blockAnchor.kind, blockAnchor.locationAnchor), {
      documentId,
      kind: blockAnchor.kind,
      locationAnchor: blockAnchor.locationAnchor,
      pdfPreviewHtml: blockAnchor.pdfPreviewHtml,
      versionId: 'live',
    });
  }
  return map;
}

/**
 * Whether the live document is populated enough to treat missing anchors as
 * real staleness.
 *
 * The Slate editor instance exists immediately; Yjs fills `editor.children`
 * later. Until that load finishes the children are a placeholder document —
 * often with paragraphs, so the anchor map is non-empty — whose ids do not
 * match the run. Judging against that placeholder flashes every suggestion
 * as stale (and can auto-rerun the playbook) on panel load.
 */
export function getIsLiveDocumentReadyToJudgeStaleness(options: {
  hasEditor: boolean;
  isEditorLoading: boolean;
  liveAnchorCount: number;
}): boolean {
  return options.hasEditor && !options.isEditorLoading && options.liveAnchorCount > 0;
}

/**
 * Whether one preview card no longer matches the live document. Each card
 * type declares what it depends on:
 *
 * - An in-paragraph edit rewrites its paragraph's exact text, so it depends
 *   on that text: a `textHash` mismatch (against the tracked-changes-applied
 *   `text` projection the appliers fingerprint, NOT `fullText`) is stale.
 * - An insertion only lands relative to its anchor — its content stands on
 *   its own whatever the anchor's wording — and a removal's hash spans a
 *   partitioning only the generating run knew. Both depend on nothing but
 *   their anchor still existing.
 * - Notes and errors reference nothing in the document.
 */
export function getIsCardStale(
  card: CodeBlockRedlineItem,
  anchorInfoByKey: Map<string, AnchorInfo>
): boolean {
  switch (card.type) {
    case 'redline-edit-error':
    case 'redline-note':
      return false;
    case 'redline-insert':
    case 'redline-remove':
      return !anchorInfoByKey.has(card.locationAnchor);
    case 'redline-edit': {
      const info = anchorInfoByKey.get(card.locationAnchor);
      if (!info || info.kind !== 'paragraph') return true;
      return Boolean(card.textHash) && hash(info.text) !== card.textHash;
    }
  }
}

/**
 * Whether one suggestion still matches the live document (ADR 0006:
 * staleness is per suggestion — accepting one suggestion can invalidate a
 * sibling that rewrites the same paragraph, without touching the rest). One
 * invalidated card stales the whole suggestion: its cards are one edit.
 */
export function getIsSuggestionStale(
  previewCards: CodeBlockRedlineItem[],
  anchorInfoByKey: Map<string, AnchorInfo>
): boolean {
  /**
   * An empty map is the editor not mounted yet, not a document with every
   * paragraph gone. Judging against it would flash every suggestion as stale
   * (and auto-rerun the playbook) on panel load.
   */
  if (anchorInfoByKey.size === 0) return false;
  return previewCards.some(card => getIsCardStale(card, anchorInfoByKey));
}
